<template>
  <ReportPageLayout>
    <template #page-header>
      <OverviewNavHeader />
    </template>

    <!-- Default slot for main content -->
    <!-- Consider wrapping this section in DataStateHandler -->
    <div v-if="loading" class="d-flex justify-center align-center py-12">
      <v-progress-circular
        indeterminate
        color="primary"
        size="64"
      ></v-progress-circular>
    </div>
    <div v-else-if="error && !report" class="error-container text-center py-8">
      <v-alert
        type="error"
        title="Error Loading Report"
        :text="error"
        class="mx-auto"
        max-width="600"
        variant="tonal"
        border="start"
      ></v-alert>
      <v-btn color="primary" class="mt-4" @click="reloadReport"> Retry </v-btn>
    </div>
    <v-responsive v-else-if="report" max-width="1320" class="mx-auto">
      <v-row>
        <v-col cols="12">
          <GatewayHeadingCard
            :report="report"
            :selected-grade="String(route.params.grade)"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" md="3">
          <GradeOverviewNav :grade="route.params.grade as string" />
        </v-col>
        <v-col cols="12" md="9">
          <div>
            <SeriesPerformanceSnapshot
              title="Summary of Alignment and Usability"
              class="mb-8"
            />

            <h4 class="font-weight-bold text-secondary mb-4">
              Gateway 1 Overview
            </h4>
            <h2 class="font-weight-bold text-secondary">
              Text Quality & Complexity and Alignment to the Standards with
              Tasks and Questions Grounded in Evidence
            </h2>

            <DataTable
              :headers="['score']"
              :hideHeader="true"
              :items="qualityAndComplexity"
              :skillName="true"
              :solidBg="true"
              classes="mt-6 mb-8"
            />

            <v-expand-transition>
              <div>
                <ReadMore>
                  The instructional materials reviewed for Grades K-2 meet
                  expectations for Alignment to NGSS, Gateways 1 and 2. Gateway
                  1: Designed for NGSS; Criterion 1: Three-Dimensional Le arning
                  meets expectations. The materials include three-dimensional
                  learning opportunities and opportunities for student
                  sensemaking with the three dimensions. The formative and
                  summative assessments consistently measure the three
                  dimensions for their respective objectives. Criterion 2:
                  Phenomena and Problems Drive Learning meets expectations.
                  Phenomena and problems are present, connected to DCIs, and
                  presented to students as directly as possible. The materials
                  consistently elicit but inconsistently leverage student prior
                  knowledge and experience related to the phenomena and problems
                  present. Phenomena and problems consistently drive learning
                  and use of the three dimensions at both the learning sequence
                  and learning opportunity levels.
                </ReadMore>
              </div>
            </v-expand-transition>

            <v-divider class="my-12"></v-divider>

            <IndicatorHeading>
              <template #title
                >Criterion 1.1: Text Quality and Complexity</template
              >
              <template #score>
                <span class="text-status-orange">
                  <span class="font-weight-bold">16</span> / 20
                </span>
              </template>
              <template #description>
                Texts are worthy of students' time and attention: texts are of
                quality and are rigorous, meeting the text complexity criteria
                for each grade. Materials support students' advancing toward
                independent reading.
              </template>
            </IndicatorHeading>

            <IndicatorBlock color="orange">
              <p>
                The grade 6 materials include anchor texts that are
                well-crafted, award-winning texts from multiple perspectives
                that are content-rich with ideas, events, and experiences. The
                materials include a higher number of informational texts than
                literary works, with a 77/23 split. While quantitative analysis
                places many texts below the 6th-grade Lexile band, qualitative
                analysis reveals that texts are at the appropriate levels of
                complexity for 6th-grade students. Materials include anchor and
                supporting texts that vary in complexity and support students’
                literacy growth over the course of the school year. There are a
                variety of text types and genres to provide a range and volume
                of reading that offer opportunities for independent reading.
              </p>
            </IndicatorBlock>

            <IndicatorBlock class="mb-8">
              <div class="block-title">
                <div class="title-text text-secondary">
                  Multilingual Learner Supports
                </div>
                <div class="score-text text-secondary">
                  <span class="font-weight-bold">3</span> / 4
                </div>
              </div>
              <p>
                Lorem ipsum dolor sit amet, consectetur adipiscing elit.
                Phasellus pulvinar imperdiet semper. Aliquam tincidunt, orci ac
                feugiat efficitur, sem sapien sagittis dolor, euismod ultrices
                metus quam sit amet lectus. Etiam mollis sit amet lectus et
                pharetra. Sed laoreet at enim nec tincidunt. Cras varius sem ac
                urna convallis, eu bibendum orci dapibus.
              </p>
            </IndicatorBlock>

            <IndicatorHeading
              :linked-title="true"
              @open-glossary="
                openGlossaryComposable('Text Quality and Complexity')
              "
            >
              <template #title>Indicator 1A</template>
              <template #sub-title>
                Multilingual Learner Supports
                <span class="sub-title-score"
                  ><span class="font-weight-bold">4</span> / 4</span
                >
              </template>
              <template #score>
                <span class="text-status-orange">
                  <span class="font-weight-bold">2</span> / 4
                </span>
              </template>
            </IndicatorHeading>
            <IndicatorHeading
              :linked-title="true"
              @open-glossary="
                openGlossaryComposable('Text Quality and Complexity')
              "
            >
              <template #title>Indicator 1B</template>
              <template #sub-title>
                Multilingual Learner Supports
                <span class="sub-title-score"
                  ><span class="font-weight-bold">1</span> / 4</span
                >
              </template>
              <template #score>
                <span class="text-status-green">
                  <span class="font-weight-bold">4</span> / 4
                </span>
              </template>
            </IndicatorHeading>
            <IndicatorHeading
              :linked-title="true"
              @open-glossary="
                openGlossaryComposable('Text Quality and Complexity')
              "
            >
              <template #title>Indicator 1C</template>
              <template #sub-title>
                Multilingual Learner Supports
                <span class="sub-title-score"
                  ><span class="font-weight-bold">2</span> / 4</span
                >
              </template>
              <template #score>
                <span class="text-status-green">
                  <span class="font-weight-bold">4</span> / 4
                </span>
              </template>
            </IndicatorHeading>
            <IndicatorHeading
              :linked-title="true"
              @open-glossary="
                openGlossaryComposable('Text Quality and Complexity')
              "
            >
              <template #title>Indicator 1D</template>
              <template #sub-title>
                Multilingual Learner Supports
                <span class="sub-title-score"
                  ><span class="font-weight-bold">2</span> / 4</span
                >
              </template>
              <template #score>
                <span class="text-status-orange">
                  <span class="font-weight-bold">2</span> / 4
                </span>
              </template>
            </IndicatorHeading>
            <IndicatorHeading
              :linked-title="true"
              :hide-border="true"
              @open-glossary="
                openGlossaryComposable('Text Quality and Complexity')
              "
            >
              <template #title>Indicator 1E</template>
              <template #sub-title>
                Multilingual Learner Supports
                <span class="sub-title-score"
                  ><span class="font-weight-bold">3</span> / 4</span
                >
              </template>
              <template #score>
                <span class="text-status-green">
                  <span class="font-weight-bold">4</span> / 4
                </span>
              </template>
            </IndicatorHeading>

            <v-divider class="my-12"></v-divider>

            <IndicatorHeading>
              <template #title>Indicator 1A</template>
              <template #score>
                <span class="text-status-orange">
                  <span class="font-weight-bold">2</span> / 4
                </span>
              </template>
              <template #description>
                Anchor texts are of high quality, worthy of careful reading, and
                consider a range of student interests.
              </template>
            </IndicatorHeading>

            <IndicatorBlock color="orange">
              <p>
                The materials reviewed for Grade 6 meet the criteria of
                Indicator 1a.
              </p>
              <p>
                Anchor texts are well-crafted, award-winning texts from multiple
                perspectives that are content-rich with ideas, events, and
                experiences. The anchor texts in each unit are engaging, easily
                relatable, and will stretch students' thinking while weaving a
                similar theme of exploring coming of age in various settings.
              </p>
              <p>
                Anchor texts are of high quality and consider a range of student
                interests, are well-crafted, content-rich, and engage students
                at their grade level. Examples include, but are not limited to,
                the following:
              </p>
              <ul>
                <li>
                  In Unit 1, Developing Resilience: The Watsons Go to
                  Birmingham—1963, students read the anchor text, The Watsons Go
                  To Birmingham-1963 by Christopher Paul Curtis, a Newbery
                  award-winning author. The text includes complex themes as the
                  reader follows Kenny, a young African-American boy as he deals
                  with both large and small coming of age moments during the
                  Civil Rights Movement.
                </li>
                <li>
                  In Unit 2, Challenging Authority: The Giver, students read the
                  anchor text, The Giver by Lois Lowry, an engaging, dystopian
                  novel that allows students to consider topics of interest.
                </li>
                <li>
                  In Unit 3, Expressing Yourself: Women in the Arts, students
                  read the anchor text, Life in Motion: An Unlikely Ballerina by
                  Misty Copeland, a memoir detailing the true story of the third
                  African-American ballerina for the American Ballet Theatre.
                  Students explore the story of a ground-breaking African
                  American woman who overcame many challenges as she rose to
                  fame in the arts.
                </li>
                <li>
                  In Unit 4, Finding Connection: The Outsiders, students read
                  the anchor text, The Outsiders by SE Hinton. The classic,
                  engaging coming-of-age novel explores themes relatable to this
                  age group.
                </li>
                <li>
                  In Unit 5, Fleeing Conflict: Refugee & The Unwanted, students
                  read two anchor texts, Refugee by Alan Gratz and The Unwanted:
                  Stories of Syrian Refugees by Don Brown, an award-winning
                  graphic novelist. The Unwanted: Stories of Syrian Refugees is
                  a graphic novel about real-life experiences that occur when
                  someone is a refugee. The second anchor text, Refugee,
                  introduces different refugees from different time periods and
                  explores how they change over the course of their individual
                  stories.
                </li>
              </ul>
            </IndicatorBlock>

            <IndicatorBlock class="mb-8">
              <div class="block-title">
                <div class="title-text text-secondary">
                  Multilingual Learner Supports
                </div>
                <div class="score-text text-secondary">
                  <span class="font-weight-bold">3</span> / 4
                </div>
              </div>
              <p>
                Lorem ipsum dolor sit amet, consectetur adipiscing elit.
                Phasellus pulvinar imperdiet semper. Aliquam tincidunt, orci ac
                feugiat efficitur, sem sapien sagittis dolor, euismod ultrices
                metus quam sit amet lectus. Etiam mollis sit amet lectus et
                pharetra. Sed laoreet at enim nec tincidunt. Cras varius sem ac
                urna convallis, eu bibendum orci dapibus.
              </p>
            </IndicatorBlock>

            <v-divider class="my-12"></v-divider>

            <IndicatorHeading>
              <template #title>Indicator 1B</template>
              <template #score>
                <span class="text-status-green">
                  <span class="font-weight-bold">4</span> / 4
                </span>
              </template>
              <template #description>
                Texts are worthy of students' time and attention: texts are of
                quality and are rigorous, meeting the text complexity criteria
                for each grade. Materials support students' advancing toward
                independent reading.
              </template>
            </IndicatorHeading>

            <IndicatorBlock color="green">
              <p>
                The grade 6 materials include anchor texts that are
                well-crafted, award-winning texts from multiple perspectives
                that are content-rich with ideas, events, and experiences. The
                materials include a higher number of informational texts than
                literary works, with a 77/23 split. While quantitative analysis
                places many texts below the 6th-grade Lexile band, qualitative
                analysis reveals that texts are at the appropriate levels of
                complexity for 6th-grade students. Materials include anchor and
                supporting texts that vary in complexity and support students’
                literacy growth over the course of the school year. There are a
                variety of text types and genres to provide a range and volume
                of reading that offer opportunities for independent reading.
              </p>
            </IndicatorBlock>

            <IndicatorBlock class="mb-8">
              <div class="block-title">
                <div class="title-text text-secondary">
                  Multilingual Learner Supports
                </div>
                <div class="score-text text-secondary">
                  <span class="font-weight-bold">2</span> / 4
                </div>
              </div>
              <p>
                Lorem ipsum dolor sit amet, consectetur adipiscing elit.
                Phasellus pulvinar imperdiet semper. Aliquam tincidunt, orci ac
                feugiat efficitur, sem sapien sagittis dolor, euismod ultrices
                metus quam sit amet lectus. Etiam mollis sit amet lectus et
                pharetra. Sed laoreet at enim nec tincidunt. Cras varius sem ac
                urna convallis, eu bibendum orci dapibus.
              </p>
              <p>
                Lorem ipsum dolor sit amet, consectetur adipiscing elit.
                Phasellus pulvinar imperdiet semper. Aliquam tincidunt, orci ac
                feugiat efficitur, sem sapien sagittis dolor, euismod ultrices
                metus quam sit amet lectus. Etiam mollis sit amet lectus et
                pharetra. Sed laoreet at enim nec tincidunt. Cras varius sem ac
                urna convallis, eu bibendum orci dapibus.
              </p>
            </IndicatorBlock>
          </div>
        </v-col>
      </v-row>
    </v-responsive>
    <div v-else class="text-center py-8">
      <v-alert
        type="info"
        title="No Report Data"
        text="No grade overview data was found."
        class="mx-auto"
        max-width="600"
      ></v-alert>
    </div>
  </ReportPageLayout>
</template>

<script setup lang="ts">
  import { computed, inject } from 'vue';
  import { useRoute } from 'vue-router';
  import { useGlossary } from '../composables/useGlossary';
  import { useReportLoader } from '../composables/useReportLoader';
  import { useDocumentTitle } from '../composables/useDocumentTitle';

  import ReportPageLayout from '../layouts/ReportPageLayout.vue';
  import Glossary from '../components/glossary/Glossary.vue';
  import OverviewNavHeader from '../components/reportOverview/OverviewNavHeader.vue';
  import GradeOverviewNav from '@/components/reportGrade/GradeOverviewNav.vue';
  import GradeHeadingCard from '@/components/reportGrade/GradeHeadingCard.vue';
  import ReadMore from '../components/shared/ReadMore.vue';
  import DataTable from '../components/shared/DataTable.vue';
  import GradeLevelSummaryRow from '../components/reportGrade/GradeOverviewSummaryRow.vue';
  import SeriesPerformanceSnapshot from '../components/reports/SeriesPerformanceSnapshot.vue';
  import IndicatorBlock from '../components/shared/IndicatorBlock.vue';
  import IndicatorHeading from '../components/shared/IndicatorHeading.vue';

  const route = useRoute();
  const { openGlossary: openGlossaryComposable } = useGlossary();
  const openGlossary = inject<() => void | undefined>('openGlossary');

  // Convert route grade param to display format (e.g., 'kindergarten' -> 'K')
  const gradeLevel = computed(() => {
    const grade = route.params.grade;
    if (typeof grade !== 'string') return 'K';

    if (grade === 'kindergarten') return 'K';

    const gradeNumber = grade.split('-')[0];
    return gradeNumber;
  });

  // Load report data
  const reportSlug = computed(() => route.params.id as string);
  const { report, loading, error, reloadReport } = useReportLoader(reportSlug);

  // TODO: Move to API/composable
  const gradeAnalysis = computed(
    () =>
      `The instructional materials for Grade ${gradeLevel.value} demonstrate strong alignment with educational standards and provide comprehensive coverage of key learning objectives. The materials show particular strength in text quality and complexity, with carefully selected readings that support student growth and engagement.

Assessment materials are well-integrated throughout the curriculum, offering multiple opportunities for both formative and summative evaluation. The materials effectively support teachers in implementing differentiated instruction and addressing diverse learning needs.

Technology integration is thoughtfully implemented, with digital resources that enhance rather than replace traditional learning methods. The materials also demonstrate strong support for building knowledge systematically, with topics that build upon each other throughout the academic year.`,
  );

  // TODO: Move to API/composable
  const publicationDetailsData = computed(() => [
    {
      title: 'Essential Resource Guide',
      isbn: '978-0-5477-2970-1',
      edition: 'Edition',
      publisher: 'Publisher',
      year: '2013',
    },
  ]);

  const tableHeaders = ['Title', 'ISBN', 'Edition', 'Publisher', 'Year'];

  // Data for the Science of Reading table
  const qualityAndComplexity = ref([
    {
      skill:
        '<span class="text-secondary">Gateway 1 -</span> <span class="text-status-green">Meets Expectations</span></span>',
      score: '<h1 class="text-status-green">91%</h1>',
      alignments: {
        score: 'text-right',
      },
    },
    {
      skill:
        '<span class="text-secondary">Criterion 1.1: Text Quality and Complexity</span>',
      subtitle:
        'Multilingual Learner Supports: <span class="font-weight-bold">3</span> / 4',
      score:
        '<span class="text-status-orange"><span class="font-weight-bold">16</span> / 20</span>',
      alignments: {
        score: 'text-right',
      },
    },
    {
      skill:
        '<span class="text-secondary">Criterion 1.2: Alignment to the Standards with Tasks and Questions Grounded in Evidence</span>',
      subtitle:
        'Multilingual Learner Supports: <span class="font-weight-bold">4</span> / 4',
      score:
        '<span class="text-status-orange"><span class="font-weight-bold">18</span> / 20</span>',
      alignments: {
        score: 'text-right',
      },
    },
    {
      skill:
        '<span class="text-secondary">Criterion 1.3: Tasks and Questions: Foundational Skills Development</span>',
      subtitle:
        'Multilingual Learner Supports: <span class="font-weight-bold">4</span> / 4',
      score:
        '<span class="text-status-green"><span class="font-weight-bold">20</span> / 20</span>',
      alignments: {
        score: 'text-right',
      },
    },
  ]);

  const gateway = computed(() => {
    const gw = route.params.gateway;
    return typeof gw === 'string' ? gw : '1';
  });

  // Set page title
  const documentTitleState = computed(() => {
    if (loading.value) return 'Loading Report...';
    if (error.value) return 'Error Loading Report';
    if (report.value?.title)
      return `${report.value.title} - Grade ${gradeLevel.value} - Gateway ${gateway.value}`;
    return 'Gateway Overview';
  });

  useDocumentTitle(documentTitleState);
</script>

<style lang="scss" scoped>
  .page-container {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
  }

  .page-content-container {
    flex: 1;
  }

  .grade-overview-section {
    padding: 24px;
    background-color: white;
    border-radius: 4px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
</style>
